// ===========================================================================
// The activity frame (contract D0), the parent's side.
//
// A panel in the group screen that takes the video grid's place while a game
// runs: a picker for whoever may start one, and then a sandboxed iframe with
// the activity page in it. The frame has no network, no storage, and no view
// of this page -- it hears only what is posted to it and says only what is
// posted back -- and this module is the whole of that conversation:
//
//   parent -> frame:  init { selfId, handle, isHost, hostId, participants, band, state }
//                     state { state, hostId, participants }
//                     event { from, data }
//   frame -> parent:  ready | set { state } | event { data } | frame { dataUrl } | stop
//
// What the frame says goes to the server as ClientMsg.ACTIVITY; what the
// server says (ACTIVITY_STATE, ACTIVITY_EVENT) comes back in through setState
// and event. Every rule about who may do what is the server's. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { ACTIVITY_CATALOG, activityAllowed, activityBySlug, ActivityOp } from '@lumi/shared';
import { acceptMessage, frameMessage, FRAME_TYPES } from '../activities/frame-protocol.js';

/**
 * @param {{
 *   band: string, mode: string, standing: boolean,
 *   selfId: string, handle: string|null,
 *   nameOf: (peerId: string) => string|null,
 *   memberCount: () => number,
 *   onOp: (op: string, slug?: string|null, data?: unknown) => void,
 * }} h
 */
export function createActivityPanel(h) {
  let isOwner = false;
  /** @type {{ slug: string, hostId: string, state: unknown, participants: string[], startedAt: number }|null} */
  let current = null;
  let iframe = null;
  let frameReady = false;
  let pickerOpen = false;

  const title = el('span', { class: 'activity-title', text: t('Activities') });
  const playing = el('span', { class: 'pill activity-playing' });
  const stopBtn = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm hidden',
    text: t('Stop'),
    title: t('End this activity for everyone'),
    onClick: () => h.onOp(ActivityOp.STOP),
  });
  const closeBtn = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm',
    text: t('Close'),
    onClick: () => {
      pickerOpen = false;
      render();
    },
  });
  const head = el('div', { class: 'activity-head' }, [title, playing, stopBtn, closeBtn]);
  const picker = el('div', { class: 'activity-picker' });
  const frameHost = el('div', { class: 'activity-frame-host' });
  const root = el('div', { class: 'activity-panel hidden' }, [head, picker, frameHost]);

  /** The owner starts things; in a room the site keeps open, anyone may. */
  const mayStart = () => isOwner || !!h.standing;
  const isHost = () => !!current && current.hostId === h.selfId;

  function participantsFor() {
    return (current?.participants || []).map((peerId) => ({
      peerId,
      handle: peerId === h.selfId ? h.handle || null : h.nameOf(peerId) || null,
    }));
  }

  function renderPicker() {
    const n = h.memberCount();
    const list = ACTIVITY_CATALOG.filter((a) => activityAllowed(a.slug, { band: h.band, mode: h.mode }));
    picker.replaceChildren(
      el('p', {
        class: 'activity-picker-intro',
        text: mayStart()
          ? 'Pick something to do together. Everyone in the room sees it; nobody has to join in.'
          : 'Only the host can start an activity here.',
      }),
      el('div', { class: 'activity-cards' }, list.map((a) => {
        const short = n < a.minPlayers;
        return el('button', {
          type: 'button',
          class: `activity-card${short ? ' is-short' : ''}`,
          disabled: !mayStart() || short ? '' : undefined,
          onClick: () => h.onOp(ActivityOp.START, a.slug),
        }, [
          el('span', { class: 'activity-card-name', text: a.name }),
          el('span', { class: 'activity-card-blurb', text: a.blurb }),
          short
            ? el('span', {
                class: 'activity-card-need',
                text: a.minPlayers === 1 ? 'Needs someone here' : `Needs ${a.minPlayers} people here`,
              })
            : null,
        ].filter(Boolean));
      })),
    );
  }

  // ---- the frame -------------------------------------------------------------

  function post(type, payload) {
    // '*' because a sandboxed frame's origin is opaque: no named origin would
    // ever match it. The window is our own iframe, built from our own URL,
    // and nothing but this page can navigate it.
    try {
      iframe?.contentWindow?.postMessage(frameMessage(type, payload), '*');
    } catch {
      /* the frame is gone or not yet up */
    }
  }

  function sendInit() {
    if (!iframe || !frameReady || !current) return;
    post('init', {
      selfId: h.selfId,
      handle: h.handle || null,
      isHost: isHost(),
      hostId: current.hostId,
      participants: participantsFor(),
      band: h.band,
      state: current.state ?? null,
    });
  }

  function destroyFrame() {
    if (iframe) iframe.remove();
    iframe = null;
    frameReady = false;
  }

  function buildFrame(slug) {
    destroyFrame();
    const entry = activityBySlug(slug);
    iframe = el('iframe', {
      class: 'activity-frame',
      // Scripts, and nothing else: no same-origin (so no storage and no view
      // of this page), no forms, no popups, no top navigation.
      sandbox: 'allow-scripts',
      src: `/activities/${encodeURIComponent(slug)}.html`,
      title: entry ? `${entry.name} activity` : 'Activity',
      referrerpolicy: 'no-referrer',
    });
    frameHost.replaceChildren(iframe);
  }

  window.addEventListener('message', (e) => {
    if (!iframe) return;
    const m = acceptMessage(e, iframe.contentWindow, FRAME_TYPES);
    if (!m) return;
    switch (m.type) {
      case 'ready':
        frameReady = true;
        sendInit();
        break;
      case 'set':
        h.onOp(ActivityOp.SET, current?.slug, m.state);
        break;
      case 'event':
        h.onOp(ActivityOp.EVENT, current?.slug, m.data);
        break;
      case 'frame':
        if (typeof m.dataUrl === 'string') h.onOp(ActivityOp.FRAME, current?.slug, { dataUrl: m.dataUrl });
        break;
      case 'stop':
        h.onOp(ActivityOp.STOP);
        break;
      default:
        break;
    }
  });

  function render() {
    const visible = !!current || pickerOpen;
    root.classList.toggle('hidden', !visible);
    root.classList.toggle('is-running', !!current);
    const entry = current ? activityBySlug(current.slug) : null;
    title.textContent = entry ? entry.name : current ? current.slug : 'Activities';
    const n = current ? current.participants.length : 0;
    playing.textContent = current ? `${n} playing` : '';
    playing.classList.toggle('hidden', !current);
    stopBtn.classList.toggle('hidden', !(current && (isHost() || isOwner)));
    closeBtn.classList.toggle('hidden', !!current);
    picker.classList.toggle('hidden', !!current);
    frameHost.classList.toggle('hidden', !current);
    if (!current && pickerOpen) renderPicker();
  }

  return {
    root,
    api: {
      /** ACTIVITY_STATE, in full. A new game (or a restarted one) gets a fresh frame. */
      setState(m) {
        const prev = current;
        current = m && m.slug ? { ...m, participants: Array.isArray(m.participants) ? m.participants : [] } : null;
        if (!current) {
          destroyFrame();
          pickerOpen = false;
          render();
          return;
        }
        const fresh = !iframe || !prev || prev.slug !== current.slug || prev.startedAt !== current.startedAt;
        if (fresh) buildFrame(current.slug);
        else if (frameReady) post('state', { state: current.state ?? null, hostId: current.hostId, participants: participantsFor() });
        render();
      },
      /** ACTIVITY_EVENT: forwarded into the frame when it is for the game on screen. */
      event(m) {
        if (!current || !frameReady || !m || m.slug !== current.slug) return;
        post('event', { from: m.from, data: m.data });
      },
      setOwner(on) {
        isOwner = !!on;
        render();
      },
      /** The picker, for whoever may start something. Toggles. */
      showPicker() {
        if (current) return;
        pickerOpen = !pickerOpen;
        render();
      },
      /** The headcount moved: a game that needed more people may now have them. */
      refreshPicker() {
        if (!current && pickerOpen) renderPicker();
      },
      isRunning: () => !!current,
      destroy() {
        destroyFrame();
        current = null;
        pickerOpen = false;
        render();
      },
    },
  };
}

export default { createActivityPanel };
